import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, EditCalendar, Visibility } from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
// NOTE: Pending Review is intentionally EXEMPT from the global cycle filter.
// Items here have no determined cycle yet (their date couldn't be
// auto-extracted) — filtering by cycle would hide every row.
import type { Id } from "../../../convex/_generated/dataModel";
import { FilePreviewDrawer } from "../components/FilePreviewDrawer";
import { DataTableShell } from "../components/table/DataTableShell";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDateTime(ms: number) {
  return new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

type FileRow = {
  _id: Id<"sourceFiles">;
  _creationTime: number;
  fileName: string;
  fileType: string;
  fileCategory: string;
  source: string;
  hash: string;
  storageId: Id<"_storage">;
  attributedDate?: string;
  dateAttribution?: string;
  reconciliationCycleId: string;
  processingStatus: string;
  canonicalName?: string;
};

type InvoiceRow = {
  _id: Id<"invoices">;
  _creationTime: number;
  vendorName: string;
  invoiceNumber?: string;
  date: string;
  amount: number;
  source: string;
  sourceReference?: string;
  ocrConfidence: number;
  fileStorageId?: Id<"_storage">;
  attributedDate?: string;
  dateAttribution?: string;
  reconciliationCycleId?: string;
};

type PreviewTarget =
  | { kind: "file"; storageId: string; title: string; subtitle?: string }
  | { kind: "invoice"; storageId: string | null; title: string; subtitle?: string };

/** Inline date-save control used in the Action column. */
function DateSaveControl({
  initialDate,
  saving,
  onSave,
}: {
  initialDate: string;
  saving: boolean;
  onSave: (date: string) => Promise<void>;
}) {
  const [date, setDate] = useState(initialDate);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        type="date"
        size="small"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        disabled={saving}
        sx={{ width: 150 }}
        onClick={(e) => e.stopPropagation()}
      />
      <Button
        size="small"
        variant="contained"
        onClick={(e) => {
          e.stopPropagation();
          if (date) onSave(date);
        }}
        disabled={saving || !date}
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
      >
        Save
      </Button>
    </Stack>
  );
}

export function PendingReview() {
  // Cross-cycle review queue: NEVER scoped by the global cycle filter.
  const files = useStableQuery(api.sourceFiles.listNeedsReview, { limit: 200 }, []) as
    | FileRow[]
    | null;
  const invoices = useStableQuery(api.invoices.listNeedsReview, { limit: 200 }, []) as
    | InvoiceRow[]
    | null;

  const filesLoading = files === undefined || files === null;
  const invoicesLoading = invoices === undefined || invoices === null;

  const filesList = files ?? [];
  const invoicesList = invoices ?? [];
  const totalCount = filesList.length + invoicesList.length;

  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [savingFileId, setSavingFileId] = useState<string | null>(null);
  const [savingInvoiceId, setSavingInvoiceId] = useState<string | null>(null);

  const setFileReviewedDate = useMutation(api.sourceFiles.setReviewedDate);
  const setInvoiceReviewedDate = useMutation(api.invoices.setReviewedDate);

  const handleFileSave = async (id: Id<"sourceFiles">, date: string) => {
    setSavingFileId(id);
    try {
      const result = await setFileReviewedDate({ id, date });
      console.info("[PendingReview] file rerouted", {
        id,
        cycleId: result.cycleId,
        reassigned: result.reassigned,
      });
    } catch (err) {
      console.error("[PendingReview] failed to save reviewed date for file", err);
    } finally {
      setSavingFileId(null);
    }
  };

  const handleInvoiceSave = async (id: Id<"invoices">, date: string) => {
    setSavingInvoiceId(id);
    try {
      const result = await setInvoiceReviewedDate({ id, date });
      console.info("[PendingReview] invoice rerouted", {
        id,
        cycleId: result.cycleId,
        reassigned: result.reassigned,
      });
    } catch (err) {
      console.error("[PendingReview] failed to save reviewed date for invoice", err);
    } finally {
      setSavingInvoiceId(null);
    }
  };

  const fileColumns: GridColDef<FileRow>[] = useMemo(
    () => [
      {
        field: "fileName",
        headerName: "File",
        flex: 2,
        minWidth: 220,
        sortable: true,
        renderCell: (p: GridRenderCellParams<FileRow>) => {
          const row = p.row;
          return (
            <Box sx={{ py: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "var(--c-text-primary)" }}
              >
                {row.fileName}
              </Typography>
              {row.canonicalName && row.canonicalName !== row.fileName && (
                <Typography
                  variant="caption"
                  sx={{ color: "var(--c-text-muted)", fontSize: "0.7rem" }}
                >
                  {row.canonicalName}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "source",
        headerName: "Source",
        width: 130,
        sortable: true,
        renderCell: (p) => (
          <Chip size="small" label={p.value as string} variant="outlined" />
        ),
      },
      {
        field: "fileCategory",
        headerName: "Category",
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <Chip size="small" label={p.value as string} variant="outlined" />
        ),
      },
      {
        field: "attributedDate",
        headerName: "Attributed Date",
        width: 150,
        sortable: true,
        valueFormatter: (value) => value ?? "—",
      },
      {
        field: "dateAttribution",
        headerName: "Attribution",
        width: 150,
        sortable: true,
        renderCell: (p) => (
          <Chip
            size="small"
            label={(p.value as string) ?? "unknown"}
            variant="outlined"
            color="warning"
          />
        ),
      },
      {
        field: "_creationTime",
        headerName: "Uploaded",
        width: 160,
        sortable: true,
        renderCell: (p) => formatDateTime(p.value as number),
      },
      {
        field: "actions",
        headerName: "Set Date",
        width: 320,
        sortable: false,
        renderCell: (p: GridRenderCellParams<FileRow>) => {
          const row = p.row;
          const initial = row.attributedDate ?? new Date().toISOString().slice(0, 10);
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="View file">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview({
                      kind: "file",
                      storageId: row.storageId as unknown as string,
                      title: row.fileName,
                      subtitle: `${row.fileCategory} · ${row.source}`,
                    });
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              <DateSaveControl
                initialDate={initial}
                saving={savingFileId === row._id}
                onSave={(date) => handleFileSave(row._id, date)}
              />
            </Stack>
          );
        },
      },
    ],
    [savingFileId],
  );

  const invoiceColumns: GridColDef<InvoiceRow>[] = useMemo(
    () => [
      {
        field: "vendorName",
        headerName: "Vendor",
        flex: 1.5,
        minWidth: 160,
        sortable: true,
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {p.value as string}
          </Typography>
        ),
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}
          >
            {(p.value as string) ?? "—"}
          </Typography>
        ),
      },
      {
        field: "date",
        headerName: "Current Date",
        width: 130,
        sortable: true,
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 130,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(p.value as number)}
          </Typography>
        ),
      },
      {
        field: "source",
        headerName: "Source",
        width: 130,
        sortable: true,
        renderCell: (p) => (
          <Chip size="small" label={p.value as string} variant="outlined" />
        ),
      },
      {
        field: "ocrConfidence",
        headerName: "OCR Conf.",
        width: 120,
        sortable: true,
        renderCell: (p) => `${Math.round(p.value as number)}%`,
      },
      {
        field: "dateAttribution",
        headerName: "Attribution",
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <Chip
            size="small"
            label={(p.value as string) ?? "unknown"}
            variant="outlined"
            color="warning"
          />
        ),
      },
      {
        field: "actions",
        headerName: "Set Date",
        width: 320,
        sortable: false,
        renderCell: (p: GridRenderCellParams<InvoiceRow>) => {
          const row = p.row;
          const initial = row.date ?? new Date().toISOString().slice(0, 10);
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={row.fileStorageId ? "View file" : "No file attached"}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!row.fileStorageId}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreview({
                        kind: "invoice",
                        storageId: (row.fileStorageId as unknown as string) ?? null,
                        title: row.vendorName,
                        subtitle: `Invoice #${row.invoiceNumber || "N/A"} · ${row.source}`,
                      });
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <DateSaveControl
                initialDate={initial}
                saving={savingInvoiceId === row._id}
                onSave={(date) => handleInvoiceSave(row._id, date)}
              />
            </Stack>
          );
        },
      },
    ],
    [savingInvoiceId],
  );

  const fileRows = filesList.map((f) => ({ ...f, id: f._id }));
  const invoiceRows = invoicesList.map((i) => ({ ...i, id: i._id }));

  const loadingAll = filesLoading && invoicesLoading;
  const bothEmpty = !loadingAll && totalCount === 0;

  return (
    <Box>
      {/* Page header — single unified header for both sections */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" data-tour="page-title-file-review">Pending Review</Typography>
          <Typography variant="body2" color="text.secondary">
            Files and invoices whose dates could not be extracted automatically.
            Set the correct date to re-route the row to the matching cycle.
          </Typography>
        </Box>
        {totalCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={`${totalCount} item${totalCount !== 1 ? "s" : ""}`}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {bothEmpty ? (
        <DataTableShell
          title=""
          rows={[]}
          columns={[]}
          itemLabel="item"
          emptyTitle="Nothing to review"
          emptyDescription="All files and invoices are correctly attributed by date — nothing to review."
          emptyIcon={
            <CheckCircleOutline
              sx={{ fontSize: 32, color: "var(--c-metric-success)" }}
            />
          }
        />
      ) : (
        <Stack spacing={4}>
          {/* Files section */}
          {(filesList.length > 0 || filesLoading) && (
            <Box data-tour="file-review-files">
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <EditCalendar sx={{ fontSize: 20, color: "var(--c-link-accent)" }} />
                <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                  Files needing manual review
                </Typography>
                {filesList.length > 0 && (
                  <Chip
                    size="small"
                    label={filesList.length}
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      bgcolor: "var(--c-overlay-4)",
                      color: "var(--c-text-secondary)",
                    }}
                  />
                )}
              </Stack>
              <DataTableShell<FileRow & { id: string }>
                title=""
                rows={fileRows as any}
                columns={fileColumns as any}
                loading={filesLoading}
                itemLabel="file"
                emptyTitle="No files waiting for review"
                emptyDescription="All files have been date-attributed and routed."
                gridProps={{
                  initialState: {
                    sorting: {
                      sortModel: [{ field: "_creationTime", sort: "desc" }],
                    },
                    pagination: { paginationModel: { pageSize: 10 } },
                  },
                  rowHeight: 68,
                }}
              />
            </Box>
          )}

          {/* Invoices section */}
          {(invoicesList.length > 0 || invoicesLoading) && (
            <Box data-tour="file-review-invoices">
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <EditCalendar sx={{ fontSize: 20, color: "var(--c-link-accent)" }} />
                <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                  Invoices needing manual review
                </Typography>
                {invoicesList.length > 0 && (
                  <Chip
                    size="small"
                    label={invoicesList.length}
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      bgcolor: "var(--c-overlay-4)",
                      color: "var(--c-text-secondary)",
                    }}
                  />
                )}
              </Stack>
              <DataTableShell<InvoiceRow & { id: string }>
                title=""
                rows={invoiceRows as any}
                columns={invoiceColumns as any}
                loading={invoicesLoading}
                itemLabel="invoice"
                emptyTitle="No invoices waiting for review"
                emptyDescription="All invoices have been date-attributed and routed."
                gridProps={{
                  initialState: {
                    sorting: {
                      sortModel: [{ field: "_creationTime", sort: "desc" }],
                    },
                    pagination: { paginationModel: { pageSize: 10 } },
                  },
                  rowHeight: 68,
                }}
              />
            </Box>
          )}
        </Stack>
      )}

      <FilePreviewDrawer
        open={preview !== null}
        onClose={() => setPreview(null)}
        storageId={preview?.storageId ?? null}
        title={preview?.title ?? ""}
        subtitle={preview?.subtitle}
      />
    </Box>
  );
}
